import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { collection, deleteField, doc, onSnapshot, runTransaction, setDoc, Transaction, writeBatch } from 'firebase/firestore';
import { db } from '../store/firebase';
import { useData } from '../store/DataContext';
import { defaultDepartments, defaultPayrollSettings, defaultPositions } from '../data/hrDefaults';
import { AttendanceRecord, EmployeeInput, EmployeeLoan, EmploymentEvent, EmploymentEventType, HrAuditLog, HrDepartment, HrDocument, HrEmployee, HrPosition, LeaveAdjustment, LeaveOpening, LeaveRequest, PayAdjustment, PayrollRun, PayrollSettings, Payslip, SalaryStructure, StatutoryRuleSet } from '../types/hr';
import { calculateEmployeePayroll, isEligibleForPeriod, ruleSetForPeriod, totalPayroll } from '../utils/payrollEngine';
import { currentPeriod, employeeName, makeId, maskAccount } from '../utils/hrFormat';
import { leaveSnapshot } from '../utils/leaveAccrual';
type Result = {
  ok: boolean;
  error?: string;
  id?: string;
  message?: string;
};
const COL = {
  employees: 'hrEmployees',
  departments: 'hrDepartments',
  positions: 'hrPositions',
  attendance: 'hrAttendance',
  leave: 'hrLeave',
  loans: 'hrLoans',
  adjustments: 'hrAdjustments',
  runs: 'hrPayrollRuns',
  periods: 'hrPayrollPeriods',
  payslips: 'hrPayslips',
  documents: 'hrDocuments',
  audit: 'hrAuditLogs',
  userLinks: 'hrUserLinks',
  settings: 'hrSettings',
  counters: 'counters'
};
const SETTINGS_ID = 'payroll';
type HrPayrollContextValue = {
  isReady: boolean;
  employees: HrEmployee[];
  departments: HrDepartment[];
  positions: HrPosition[];
  attendance: AttendanceRecord[];
  leaveRequests: LeaveRequest[];
  loans: EmployeeLoan[];
  adjustments: PayAdjustment[];
  payrollRuns: PayrollRun[];
  payslips: Payslip[];
  documents: HrDocument[];
  auditLogs: HrAuditLog[];
  settings: PayrollSettings;
  saveDepartment: (dept: Omit<HrDepartment, 'id' | 'createdAt'> & {
    id?: string;
  }) => Promise<Result>;
  deleteDepartment: (id: string) => Promise<Result>;
  savePosition: (pos: Omit<HrPosition, 'id' | 'createdAt'> & {
    id?: string;
  }) => Promise<Result>;
  deletePosition: (id: string) => Promise<Result>;
  registerEmployee: (input: EmployeeInput) => Promise<Result>;
  updateEmployee: (id: string, patch: Partial<EmployeeInput>, summary: string) => Promise<Result>;
  changeSalary: (id: string, structure: Omit<SalaryStructure, 'changedAt' | 'changedBy' | 'changedByName'>) => Promise<Result>;
  recordEmploymentEvent: (id: string, type: EmploymentEventType, date: string, details: string, patch?: Partial<EmployeeInput>) => Promise<Result>;
  addAttendance: (record: Omit<AttendanceRecord, 'id' | 'status' | 'recordedAt' | 'recordedByName'>) => Promise<Result>;
  decideAttendance: (id: string, status: 'Approved' | 'Rejected') => Promise<Result>;
  deleteAttendance: (id: string) => Promise<Result>;
  addLeave: (request: Omit<LeaveRequest, 'id' | 'status' | 'recordedAt' | 'recordedByName'>) => Promise<Result>;
  decideLeave: (id: string, status: 'Approved' | 'Rejected' | 'Cancelled') => Promise<Result>;
  setLeaveOpening: (employeeId: string, days: number, asOf: string) => Promise<Result>;
  addLeaveAdjustment: (employeeId: string, adjustment: {
    date: string;
    days: number;
    reason: string;
  }) => Promise<Result>;
  addLoan: (loan: Omit<EmployeeLoan, 'id' | 'balance' | 'status' | 'repayments' | 'createdAt' | 'createdByName'>) => Promise<Result>;
  cancelLoan: (id: string, reason: string) => Promise<Result>;
  addAdjustment: (item: Omit<PayAdjustment, 'id' | 'createdAt' | 'createdByName' | 'runId'>) => Promise<Result>;
  deleteAdjustment: (id: string) => Promise<Result>;
  uploadDocument: (document: Omit<HrDocument, 'id' | 'uploadedAt' | 'uploadedByName'>) => Promise<Result>;
  deleteDocument: (id: string) => Promise<Result>;
  saveSettings: (patch: Partial<Omit<PayrollSettings, 'statutoryRuleSets'>>) => Promise<Result>;
  addRuleSet: (rules: Omit<StatutoryRuleSet, 'id' | 'createdAt' | 'createdByName'>) => Promise<Result>;
  deleteRuleSet: (id: string) => Promise<Result>;
  calculatePayroll: (period: string, paymentDate: string) => Promise<Result>;
  returnToDraft: (runId: string) => Promise<Result>;
  approvePayroll: (runId: string) => Promise<Result>;
  payEmployees: (runId: string) => Promise<Result>;
  confirmPayment: (runId: string, paymentReference: string, paidDate: string) => Promise<Result>;
  reversePayroll: (runId: string, reason: string) => Promise<Result>;
};
const HrPayrollContext = createContext<HrPayrollContextValue | undefined>(undefined);
const clean = <T extends object,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const failure = (error: unknown, fallback: string): Result => {
  console.error(fallback, error);
  return {
    ok: false,
    error: error instanceof Error && error.message ? error.message : fallback
  };
};
const slug = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/**
 * HR & Payroll data for the System Administrator. Nothing is subscribed or
 * written for any other role, so HR data never reaches other dashboards.
 */
export function HrPayrollProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    currentUser
  } = useData();
  const isAdmin = currentUser?.role === 'Administrator';
  const [loaded, setLoaded] = useState<Record<string, boolean>>({});
  const [employees, setEmployees] = useState<HrEmployee[]>([]);
  const [departments, setDepartments] = useState<HrDepartment[]>([]);
  const [positions, setPositions] = useState<HrPosition[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [loans, setLoans] = useState<EmployeeLoan[]>([]);
  const [adjustments, setAdjustments] = useState<PayAdjustment[]>([]);
  const [payrollRuns, setPayrollRuns] = useState<PayrollRun[]>([]);
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [documents, setDocuments] = useState<HrDocument[]>([]);
  const [auditLogs, setAuditLogs] = useState<HrAuditLog[]>([]);
  const [settings, setSettings] = useState<PayrollSettings>(defaultPayrollSettings);
  useEffect(() => {
    if (!isAdmin) return;
    const subscribe = <T,>(name: string, setter: (rows: T[]) => void, onFirst?: (empty: boolean) => void) => {
      let first = true;
      return onSnapshot(collection(db, name), snapshot => {
        setter(snapshot.docs.map(d => ({
          ...d.data(),
          id: d.id
        }) as T));
        setLoaded(prev => prev[name] ? prev : {
          ...prev,
          [name]: true
        });
        if (first && !snapshot.metadata.fromCache) {
          first = false;
          onFirst?.(snapshot.empty);
        }
      }, error => console.error(`HR subscription failed (${name})`, error));
    };
    const unsubs = [subscribe<HrEmployee>(COL.employees, setEmployees), subscribe<HrDepartment>(COL.departments, setDepartments, empty => {
      if (empty) void seedStructure();
    }), subscribe<HrPosition>(COL.positions, setPositions), subscribe<AttendanceRecord>(COL.attendance, setAttendance), subscribe<LeaveRequest>(COL.leave, setLeaveRequests), subscribe<EmployeeLoan>(COL.loans, setLoans), subscribe<PayAdjustment>(COL.adjustments, setAdjustments), subscribe<PayrollRun>(COL.runs, setPayrollRuns), subscribe<Payslip>(COL.payslips, setPayslips), subscribe<HrDocument>(COL.documents, setDocuments), subscribe<HrAuditLog>(COL.audit, setAuditLogs), onSnapshot(doc(db, COL.settings, SETTINGS_ID), snapshot => {
      if (snapshot.exists()) {
        setSettings({
          ...defaultPayrollSettings,
          ...(snapshot.data() as PayrollSettings)
        });
      } else if (!snapshot.metadata.fromCache) {
        void setDoc(doc(db, COL.settings, SETTINGS_ID), clean(defaultPayrollSettings));
      }
      setLoaded(prev => ({
        ...prev,
        settings: true
      }));
    }, error => console.error('HR settings subscription failed', error))];
    return () => unsubs.forEach(unsub => unsub());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);
  const actor = () => {
    if (!currentUser || currentUser.role !== 'Administrator') throw new Error('Only the System Administrator can manage HR & Payroll.');
    return currentUser;
  };
  const auditEntry = (entry: Omit<HrAuditLog, 'id' | 'at' | 'userId' | 'userName'>): HrAuditLog => {
    const user = actor();
    return clean({
      ...entry,
      id: makeId('hra'),
      at: new Date().toISOString(),
      userId: user.id,
      userName: user.fullName
    });
  };
  const writeAudit = (tx: Transaction, entry: Omit<HrAuditLog, 'id' | 'at' | 'userId' | 'userName'>) => {
    const log = auditEntry(entry);
    tx.set(doc(db, COL.audit, log.id), log);
  };
  /** Simple write + audit in one atomic batch. */
  const commit = async (writes: (batch: ReturnType<typeof writeBatch>) => void, audit: Omit<HrAuditLog, 'id' | 'at' | 'userId' | 'userName'>, fallback: string): Promise<Result> => {
    try {
      const batch = writeBatch(db);
      writes(batch);
      const log = auditEntry(audit);
      batch.set(doc(db, COL.audit, log.id), log);
      await batch.commit();
      return {
        ok: true,
        id: audit.entityId
      };
    } catch (error) {
      return failure(error, fallback);
    }
  };
  const seedStructure = async () => {
    try {
      const batch = writeBatch(db);
      const now = new Date().toISOString();
      for (const dept of defaultDepartments) {
        batch.set(doc(db, COL.departments, `dept-${slug(dept.name)}`), {
          name: dept.name,
          category: dept.category,
          createdAt: now
        });
      }
      for (const pos of defaultPositions) {
        const dept = defaultDepartments.find(d => d.name === pos.department)!;
        batch.set(doc(db, COL.positions, `pos-${slug(pos.title)}`), {
          title: pos.title,
          departmentId: `dept-${slug(pos.department)}`,
          category: dept.category,
          createdAt: now
        });
      }
      await batch.commit();
    } catch (error) {
      console.error('Seeding HR departments failed', error);
    }
  };

  // ─── Departments & positions ───
  const saveDepartment: HrPayrollContextValue['saveDepartment'] = async dept => {
    const name = dept.name.trim();
    if (!name) return {
      ok: false,
      error: 'Department name is required.'
    };
    if (departments.some(d => d.name.toLowerCase() === name.toLowerCase() && d.id !== dept.id)) return {
      ok: false,
      error: 'A department with this name already exists.'
    };
    const id = dept.id ?? `dept-${slug(name)}-${Date.now().toString(36)}`;
    const record = clean({
      ...dept,
      id: undefined,
      name,
      createdAt: departments.find(d => d.id === id)?.createdAt ?? new Date().toISOString()
    });
    return commit(batch => batch.set(doc(db, COL.departments, id), record), {
      action: dept.id ? 'Department updated' : 'Department created',
      entity: 'Department',
      entityId: id,
      summary: name
    }, 'The department could not be saved.');
  };
  const deleteDepartment: HrPayrollContextValue['deleteDepartment'] = async id => {
    if (employees.some(e => e.departmentId === id)) return {
      ok: false,
      error: 'Move employees out of this department first.'
    };
    if (positions.some(p => p.departmentId === id)) return {
      ok: false,
      error: 'Delete or move this department’s positions first.'
    };
    return commit(batch => batch.delete(doc(db, COL.departments, id)), {
      action: 'Department deleted',
      entity: 'Department',
      entityId: id,
      summary: departments.find(d => d.id === id)?.name ?? id
    }, 'The department could not be deleted.');
  };
  const savePosition: HrPayrollContextValue['savePosition'] = async pos => {
    const title = pos.title.trim();
    if (!title || !pos.departmentId) return {
      ok: false,
      error: 'Title and department are required.'
    };
    const id = pos.id ?? `pos-${slug(title)}-${Date.now().toString(36)}`;
    const record = clean({
      ...pos,
      id: undefined,
      title,
      createdAt: positions.find(p => p.id === id)?.createdAt ?? new Date().toISOString()
    });
    return commit(batch => batch.set(doc(db, COL.positions, id), record), {
      action: pos.id ? 'Position updated' : 'Position created',
      entity: 'Position',
      entityId: id,
      summary: title
    }, 'The position could not be saved.');
  };
  const deletePosition: HrPayrollContextValue['deletePosition'] = async id => {
    if (employees.some(e => e.positionId === id)) return {
      ok: false,
      error: 'Employees still hold this position.'
    };
    return commit(batch => batch.delete(doc(db, COL.positions, id)), {
      action: 'Position deleted',
      entity: 'Position',
      entityId: id,
      summary: positions.find(p => p.id === id)?.title ?? id
    }, 'The position could not be deleted.');
  };

  // ─── Employees ───
  const registerEmployee: HrPayrollContextValue['registerEmployee'] = async input => {
    try {
      const user = actor();
      if (!input.firstName.trim() || !input.surname.trim()) return {
        ok: false,
        error: 'First name and surname are required.'
      };
      if (!input.departmentId || !input.positionId) return {
        ok: false,
        error: 'Department and position are required.'
      };
      const id = await runTransaction(db, async tx => {
        const counterRef = doc(db, COL.counters, 'hrEmployeeNumber');
        const linkRef = input.userId ? doc(db, COL.userLinks, input.userId) : null;
        const [counterSnap, linkSnap] = await Promise.all([tx.get(counterRef), linkRef ? tx.get(linkRef) : Promise.resolve(null)]);
        if (linkSnap?.exists()) throw new Error(`This system account is already linked to ${linkSnap.data().employeeNumber}.`);
        const next = (Number(counterSnap.data()?.last) || 0) + 1;
        const employeeNumber = `${settings.employeePrefix}-${String(next).padStart(4, '0')}`;
        const employeeId = `emp-${String(next).padStart(4, '0')}`;
        const now = new Date().toISOString();
        const startPeriod = (input.startDate || now).slice(0, 7);
        const employee: HrEmployee = {
          ...input,
          id: employeeId,
          employeeNumber,
          salaryHistory: [{
            effectiveFrom: startPeriod,
            basicSalary: input.basicSalary,
            salaryGrade: input.salaryGrade,
            earnings: input.earnings,
            deductions: input.deductions,
            changedAt: now,
            changedBy: user.id,
            changedByName: user.fullName,
            reason: 'Initial salary on registration'
          }],
          events: [{
            id: makeId('evt'),
            type: 'Hired',
            date: input.startDate || now.slice(0, 10),
            details: `Registered as ${employeeNumber}`,
            recordedAt: now,
            recordedByName: user.fullName
          }],
          createdAt: now,
          createdByName: user.fullName,
          updatedAt: now
        };
        tx.set(counterRef, {
          last: next,
          updatedAt: now
        });
        tx.set(doc(db, COL.employees, employeeId), clean(employee));
        if (linkRef) tx.set(linkRef, {
          employeeId,
          employeeNumber,
          linkedAt: now
        });
        writeAudit(tx, {
          action: 'Employee registered',
          entity: 'Employee',
          entityId: employeeId,
          employeeId,
          summary: `${employeeNumber} ${employeeName(input)} — basic K${input.basicSalary}`
        });
        return employeeId;
      });
      return {
        ok: true,
        id
      };
    } catch (error) {
      return failure(error, 'The employee could not be registered.');
    }
  };
  const updateEmployee: HrPayrollContextValue['updateEmployee'] = async (id, patch, summary) => {
    try {
      actor();
      await runTransaction(db, async tx => {
        const ref = doc(db, COL.employees, id);
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error('Employee not found.');
        const current = snap.data() as HrEmployee;
        const userChanged = 'userId' in patch && patch.userId !== current.userId;
        if (userChanged && patch.userId) {
          const linkSnap = await tx.get(doc(db, COL.userLinks, patch.userId));
          if (linkSnap.exists() && linkSnap.data().employeeId !== id) throw new Error(`This system account is already linked to ${linkSnap.data().employeeNumber}.`);
        }
        const now = new Date().toISOString();
        const bankFields = ['bankName', 'bankAccountName', 'bankAccountNumber', 'bankBranch'] as const;
        const bankChanged = bankFields.some(field => field in patch && patch[field] !== current[field]);
        const update: Record<string, unknown> = {
          ...clean(patch),
          updatedAt: now
        };
        if ('userId' in patch && !patch.userId) update.userId = deleteField();
        tx.update(ref, update);
        if (userChanged) {
          if (current.userId) tx.delete(doc(db, COL.userLinks, current.userId));
          if (patch.userId) tx.set(doc(db, COL.userLinks, patch.userId), {
            employeeId: id,
            employeeNumber: current.employeeNumber,
            linkedAt: now
          });
        }
        writeAudit(tx, {
          action: bankChanged ? 'Bank details changed' : 'Employee updated',
          entity: 'Employee',
          entityId: id,
          employeeId: id,
          summary,
          ...(bankChanged ? {
            before: `${current.bankName ?? '—'} ${maskAccount(current.bankAccountNumber)}`,
            after: `${patch.bankName ?? current.bankName ?? '—'} ${maskAccount(patch.bankAccountNumber ?? current.bankAccountNumber)}`
          } : {})
        });
      });
      return {
        ok: true,
        id
      };
    } catch (error) {
      return failure(error, 'The employee could not be updated.');
    }
  };
  const changeSalary: HrPayrollContextValue['changeSalary'] = async (id, structure) => {
    try {
      const user = actor();
      if (!(structure.basicSalary > 0)) return {
        ok: false,
        error: 'Basic salary must be greater than zero.'
      };
      if (!structure.reason.trim()) return {
        ok: false,
        error: 'A reason for the salary change is required.'
      };
      await runTransaction(db, async tx => {
        const ref = doc(db, COL.employees, id);
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error('Employee not found.');
        const current = snap.data() as HrEmployee;
        const now = new Date().toISOString();
        const entry: SalaryStructure = {
          ...structure,
          changedAt: now,
          changedBy: user.id,
          changedByName: user.fullName
        };
        const history = [...(current.salaryHistory ?? []), entry];
        const inForce = history.filter(item => item.effectiveFrom <= currentPeriod()).sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom) || b.changedAt.localeCompare(a.changedAt))[0];
        tx.update(ref, clean({
          salaryHistory: history,
          updatedAt: now,
          ...(inForce ? {
            basicSalary: inForce.basicSalary,
            salaryGrade: inForce.salaryGrade ?? null,
            earnings: inForce.earnings,
            deductions: inForce.deductions
          } : {})
        }));
        writeAudit(tx, {
          action: 'Salary changed',
          entity: 'Employee',
          entityId: id,
          employeeId: id,
          summary: `Effective ${structure.effectiveFrom}: ${structure.reason}`,
          before: `Basic K${current.basicSalary}`,
          after: `Basic K${structure.basicSalary}`
        });
      });
      return {
        ok: true,
        id
      };
    } catch (error) {
      return failure(error, 'The salary change could not be saved.');
    }
  };
  const recordEmploymentEvent: HrPayrollContextValue['recordEmploymentEvent'] = async (id, type, date, details, patch = {}) => {
    try {
      const user = actor();
      if (!details.trim()) return {
        ok: false,
        error: 'Details are required.'
      };
      await runTransaction(db, async tx => {
        const ref = doc(db, COL.employees, id);
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error('Employee not found.');
        const current = snap.data() as HrEmployee;
        const event: EmploymentEvent = {
          id: makeId('evt'),
          type,
          date,
          details,
          recordedAt: new Date().toISOString(),
          recordedByName: user.fullName
        };
        tx.update(ref, clean({
          ...patch,
          events: [...(current.events ?? []), event],
          updatedAt: event.recordedAt
        }));
        writeAudit(tx, {
          action: type,
          entity: 'Employee',
          entityId: id,
          employeeId: id,
          summary: details
        });
      });
      return {
        ok: true,
        id
      };
    } catch (error) {
      return failure(error, 'The employment record could not be saved.');
    }
  };

  // ─── Attendance & leave ───
  const addAttendance: HrPayrollContextValue['addAttendance'] = async record => {
    if (!record.employeeId || !record.date) return {
      ok: false,
      error: 'Employee and date are required.'
    };
    if (record.shiftType === 'Overtime' && !(Number(record.hours) > 0)) return {
      ok: false,
      error: 'Enter the overtime hours worked.'
    };
    if (attendance.some(a => a.employeeId === record.employeeId && a.date === record.date && a.shiftType === record.shiftType && a.status !== 'Rejected')) return {
      ok: false,
      error: 'This shift is already recorded for that date.'
    };
    const id = makeId('att');
    const user = currentUser!;
    return commit(batch => batch.set(doc(db, COL.attendance, id), clean({
      ...record,
      status: 'Pending',
      recordedAt: new Date().toISOString(),
      recordedByName: user.fullName
    })), {
      action: 'Attendance recorded',
      entity: 'Attendance',
      entityId: id,
      employeeId: record.employeeId,
      summary: `${record.shiftType} on ${record.date}`
    }, 'The attendance record could not be saved.');
  };
  const decideAttendance: HrPayrollContextValue['decideAttendance'] = async (id, status) => {
    const record = attendance.find(a => a.id === id);
    if (!record) return {
      ok: false,
      error: 'Record not found.'
    };
    if (payrollRuns.some(run => run.status !== 'Reversed' && run.status !== 'Draft' && record.date.startsWith(run.period))) return {
      ok: false,
      error: 'Payroll for this month is already approved or finalised.'
    };
    return commit(batch => batch.update(doc(db, COL.attendance, id), {
      status,
      decidedAt: new Date().toISOString(),
      decidedByName: currentUser!.fullName
    }), {
      action: `Attendance ${status.toLowerCase()}`,
      entity: 'Attendance',
      entityId: id,
      employeeId: record.employeeId,
      summary: `${record.shiftType} on ${record.date}`
    }, 'The attendance decision could not be saved.');
  };
  const deleteAttendance: HrPayrollContextValue['deleteAttendance'] = async id => {
    const record = attendance.find(a => a.id === id);
    if (!record || record.status !== 'Pending') return {
      ok: false,
      error: 'Only pending records can be deleted.'
    };
    return commit(batch => batch.delete(doc(db, COL.attendance, id)), {
      action: 'Attendance deleted',
      entity: 'Attendance',
      entityId: id,
      employeeId: record.employeeId,
      summary: `${record.shiftType} on ${record.date}`
    }, 'The record could not be deleted.');
  };
  const addLeave: HrPayrollContextValue['addLeave'] = async request => {
    if (!request.employeeId || !request.leaveTypeId) return {
      ok: false,
      error: 'Employee and leave type are required.'
    };
    if (request.endDate < request.startDate) return {
      ok: false,
      error: 'End date is before start date.'
    };
    if (request.days <= 0) return {
      ok: false,
      error: 'The selected dates contain no working days.'
    };
    const id = makeId('lv');
    return commit(batch => batch.set(doc(db, COL.leave, id), clean({
      ...request,
      status: 'Pending',
      recordedAt: new Date().toISOString(),
      recordedByName: currentUser!.fullName
    })), {
      action: 'Leave requested',
      entity: 'Leave',
      entityId: id,
      employeeId: request.employeeId,
      summary: `${request.days} day(s) from ${request.startDate}`
    }, 'The leave request could not be saved.');
  };
  const decideLeave: HrPayrollContextValue['decideLeave'] = async (id, status) => {
    const request = leaveRequests.find(l => l.id === id);
    if (!request) return {
      ok: false,
      error: 'Leave request not found.'
    };
    return commit(batch => batch.update(doc(db, COL.leave, id), {
      status,
      decidedAt: new Date().toISOString(),
      decidedByName: currentUser!.fullName
    }), {
      action: `Leave ${status.toLowerCase()}`,
      entity: 'Leave',
      entityId: id,
      employeeId: request.employeeId,
      summary: `${request.days} day(s) from ${request.startDate}`
    }, 'The leave decision could not be saved.');
  };

  // ─── Loans, adjustments, documents ───
  const addLoan: HrPayrollContextValue['addLoan'] = async loan => {
    if (!(loan.amount > 0)) return {
      ok: false,
      error: 'Amount must be greater than zero.'
    };
    if (!(loan.monthlyInstalment > 0) || loan.monthlyInstalment > loan.amount) return {
      ok: false,
      error: 'Monthly repayment must be above zero and not more than the amount issued.'
    };
    const id = makeId('loan');
    return commit(batch => batch.set(doc(db, COL.loans, id), clean({
      ...loan,
      balance: loan.amount,
      status: 'Active',
      repayments: [],
      createdAt: new Date().toISOString(),
      createdByName: currentUser!.fullName
    })), {
      action: `${loan.type} issued`,
      entity: 'Loan',
      entityId: id,
      employeeId: loan.employeeId,
      summary: `K${loan.amount} at K${loan.monthlyInstalment}/month from ${loan.repaymentStart}`
    }, 'The loan could not be saved.');
  };
  const cancelLoan: HrPayrollContextValue['cancelLoan'] = async (id, reason) => {
    const loan = loans.find(l => l.id === id);
    if (!loan || loan.status !== 'Active') return {
      ok: false,
      error: 'Only active loans can be cancelled.'
    };
    if (!reason.trim()) return {
      ok: false,
      error: 'A reason is required.'
    };
    return commit(batch => batch.update(doc(db, COL.loans, id), {
      status: 'Cancelled',
      notes: reason
    }), {
      action: 'Loan cancelled',
      entity: 'Loan',
      entityId: id,
      employeeId: loan.employeeId,
      summary: reason
    }, 'The loan could not be cancelled.');
  };
  const addAdjustment: HrPayrollContextValue['addAdjustment'] = async item => {
    if (!(item.amount > 0)) return {
      ok: false,
      error: 'Amount must be greater than zero.'
    };
    if (payrollRuns.some(run => run.period === item.period && ['Approved', 'Processed', 'Paid'].includes(run.status))) return {
      ok: false,
      error: 'Payroll for this month is already approved or finalised.'
    };
    const id = makeId('adj');
    return commit(batch => batch.set(doc(db, COL.adjustments, id), clean({
      ...item,
      createdAt: new Date().toISOString(),
      createdByName: currentUser!.fullName
    })), {
      action: `Variable ${item.kind} added`,
      entity: 'Adjustment',
      entityId: id,
      employeeId: item.employeeId,
      summary: `${item.typeId} K${item.amount} for ${item.period}`
    }, 'The entry could not be saved.');
  };
  const deleteAdjustment: HrPayrollContextValue['deleteAdjustment'] = async id => {
    const item = adjustments.find(a => a.id === id);
    if (!item || item.runId) return {
      ok: false,
      error: 'Entries already paid in a payroll cannot be deleted.'
    };
    if (payrollRuns.some(run => run.period === item.period && run.status === 'Approved')) return {
      ok: false,
      error: 'Return the approved payroll to draft first.'
    };
    return commit(batch => batch.delete(doc(db, COL.adjustments, id)), {
      action: `Variable ${item.kind} removed`,
      entity: 'Adjustment',
      entityId: id,
      employeeId: item.employeeId,
      summary: `${item.typeId} K${item.amount} for ${item.period}`
    }, 'The entry could not be deleted.');
  };
  const uploadDocument: HrPayrollContextValue['uploadDocument'] = async document => {
    const id = makeId('doc');
    return commit(batch => batch.set(doc(db, COL.documents, id), clean({
      ...document,
      uploadedAt: new Date().toISOString(),
      uploadedByName: currentUser!.fullName
    })), {
      action: 'Document uploaded',
      entity: 'Document',
      entityId: id,
      employeeId: document.employeeId,
      summary: `${document.category}: ${document.title}`
    }, 'The document could not be uploaded.');
  };
  const deleteDocument: HrPayrollContextValue['deleteDocument'] = async id => {
    const item = documents.find(d => d.id === id);
    if (!item) return {
      ok: false,
      error: 'Document not found.'
    };
    return commit(batch => batch.delete(doc(db, COL.documents, id)), {
      action: 'Document deleted',
      entity: 'Document',
      entityId: id,
      employeeId: item.employeeId,
      summary: `${item.category}: ${item.title}`
    }, 'The document could not be deleted.');
  };

  // ─── Leave accrual (opening balance + append-only adjustments) ───
  const setLeaveOpening: HrPayrollContextValue['setLeaveOpening'] = async (employeeId, days, asOf) => {
    try {
      const user = actor();
      if (!Number.isFinite(days) || days < 0) return {
        ok: false,
        error: 'Opening balance must be zero or more days.'
      };
      if (!asOf) return {
        ok: false,
        error: 'Choose the date the opening balance applies from.'
      };
      await runTransaction(db, async tx => {
        const ref = doc(db, COL.employees, employeeId);
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error('Employee not found.');
        const current = snap.data() as HrEmployee;
        const now = new Date().toISOString();
        const opening: LeaveOpening = {
          days: Math.round(days * 10) / 10,
          asOf,
          recordedAt: now,
          recordedByName: user.fullName
        };
        tx.update(ref, {
          leaveOpening: opening,
          updatedAt: now
        });
        writeAudit(tx, {
          action: 'Leave opening balance set',
          entity: 'Leave',
          entityId: employeeId,
          employeeId,
          summary: `${current.employeeNumber}: opening balance as at ${asOf}`,
          before: current.leaveOpening ? `${current.leaveOpening.days} days as at ${current.leaveOpening.asOf}` : 'None',
          after: `${opening.days} days as at ${asOf}`
        });
      });
      return {
        ok: true,
        id: employeeId
      };
    } catch (error) {
      return failure(error, 'The opening leave balance could not be saved.');
    }
  };
  const addLeaveAdjustment: HrPayrollContextValue['addLeaveAdjustment'] = async (employeeId, adjustment) => {
    try {
      const user = actor();
      if (!Number.isFinite(adjustment.days) || adjustment.days === 0) return {
        ok: false,
        error: 'Enter a non-zero number of days.'
      };
      if (!adjustment.date) return {
        ok: false,
        error: 'Choose the date of the adjustment.'
      };
      if (!adjustment.reason.trim()) return {
        ok: false,
        error: 'A reason for the adjustment is required.'
      };
      await runTransaction(db, async tx => {
        const ref = doc(db, COL.employees, employeeId);
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error('Employee not found.');
        const current = snap.data() as HrEmployee;
        const entry: LeaveAdjustment = {
          id: makeId('ladj'),
          date: adjustment.date,
          days: Math.round(adjustment.days * 10) / 10,
          reason: adjustment.reason.trim(),
          recordedAt: new Date().toISOString(),
          recordedByName: user.fullName
        };
        tx.update(ref, {
          leaveAdjustments: [...(current.leaveAdjustments ?? []), entry],
          updatedAt: entry.recordedAt
        });
        writeAudit(tx, {
          action: 'Leave balance adjusted',
          entity: 'Leave',
          entityId: entry.id,
          employeeId,
          summary: `${current.employeeNumber}: ${entry.days > 0 ? '+' : ''}${entry.days} day(s) on ${entry.date} — ${entry.reason}`
        });
      });
      return {
        ok: true,
        id: employeeId
      };
    } catch (error) {
      return failure(error, 'The leave adjustment could not be saved.');
    }
  };

  // ─── Settings ───
  const saveSettings: HrPayrollContextValue['saveSettings'] = async patch => commit(batch => batch.set(doc(db, COL.settings, SETTINGS_ID), clean({
    ...patch,
    updatedAt: new Date().toISOString(),
    updatedByName: currentUser!.fullName
  }), {
    merge: true
  }), {
    action: 'Payroll settings changed',
    entity: 'Settings',
    entityId: SETTINGS_ID,
    summary: Object.keys(patch).join(', ')
  }, 'Settings could not be saved.');
  const addRuleSet: HrPayrollContextValue['addRuleSet'] = async rules => {
    if (settings.statutoryRuleSets.some(r => r.effectiveFrom === rules.effectiveFrom)) return {
      ok: false,
      error: 'A rule set already starts in that month. Choose another effective month.'
    };
    const bands = rules.payeBands;
    if (bands.length === 0 || bands[bands.length - 1].upTo !== null) return {
      ok: false,
      error: 'The last PAYE band must have no upper limit.'
    };
    const ruleSet: StatutoryRuleSet = {
      ...rules,
      id: `rules-${rules.effectiveFrom}`,
      createdAt: new Date().toISOString(),
      createdByName: currentUser!.fullName
    };
    return commit(batch => batch.set(doc(db, COL.settings, SETTINGS_ID), clean({
      statutoryRuleSets: [...settings.statutoryRuleSets, ruleSet]
    }), {
      merge: true
    }), {
      action: 'Statutory rules added',
      entity: 'Settings',
      entityId: ruleSet.id,
      summary: `${rules.label} effective ${rules.effectiveFrom}`
    }, 'The statutory rules could not be saved.');
  };
  const deleteRuleSet: HrPayrollContextValue['deleteRuleSet'] = async id => {
    if (payrollRuns.some(run => run.ruleSnapshot?.id === id && run.status !== 'Reversed')) return {
      ok: false,
      error: 'This rule set has been used by a payroll and must be kept.'
    };
    if (settings.statutoryRuleSets.length <= 1) return {
      ok: false,
      error: 'At least one rule set is required.'
    };
    return commit(batch => batch.set(doc(db, COL.settings, SETTINGS_ID), {
      statutoryRuleSets: settings.statutoryRuleSets.filter(r => r.id !== id)
    }, {
      merge: true
    }), {
      action: 'Statutory rules removed',
      entity: 'Settings',
      entityId: id,
      summary: id
    }, 'The rule set could not be removed.');
  };

  // ─── Payroll ───
  const calculatePayroll: HrPayrollContextValue['calculatePayroll'] = async (period, paymentDate) => {
    try {
      const user = actor();
      const rules = ruleSetForPeriod(settings.statutoryRuleSets, period);
      if (!rules) return {
        ok: false,
        error: `No statutory rule set is effective for ${period}. Add one in Payroll Settings.`
      };
      const eligible = employees.filter(employee => isEligibleForPeriod(employee, period));
      if (eligible.length === 0) return {
        ok: false,
        error: 'No eligible active employees for this month.'
      };
      const usableAdjustments = adjustments.filter(a => !a.runId);
      const lines = eligible.map(employee => calculateEmployeePayroll({
        employee,
        period,
        rules,
        settings,
        departments,
        positions,
        attendance,
        leave: leaveRequests,
        loans,
        adjustments: usableAdjustments
      })).sort((a, b) => a.employeeNumber.localeCompare(b.employeeNumber));
      const id = await runTransaction(db, async tx => {
        const periodRef = doc(db, COL.periods, period);
        const periodSnap = await tx.get(periodRef);
        const lock = periodSnap.data() as {
          activeRunId: string | null;
          revisions: number;
        } | undefined;
        let revision = (lock?.revisions ?? 0) + 1;
        let runId = `run-${period}-r${revision}`;
        if (lock?.activeRunId) {
          const runSnap = await tx.get(doc(db, COL.runs, lock.activeRunId));
          const existing = runSnap.data() as PayrollRun | undefined;
          if (existing && existing.status === 'Approved') throw new Error('This payroll is approved. Return it to draft before recalculating.');
          if (existing && (existing.status === 'Processed' || existing.status === 'Paid')) throw new Error(`Payroll for ${period} is already finalised (${existing.reference}).`);
          if (existing && existing.status === 'Draft') {
            runId = existing.id;
            revision = existing.revision;
          }
        }
        const now = new Date().toISOString();
        const run: PayrollRun = {
          id: runId,
          period,
          revision,
          status: 'Draft',
          paymentDate,
          lines,
          totals: totalPayroll(lines),
          ruleSnapshot: rules,
          calculatedAt: now,
          calculatedByName: user.fullName
        };
        tx.set(doc(db, COL.runs, runId), clean(run));
        tx.set(periodRef, {
          activeRunId: runId,
          revisions: Math.max(revision, lock?.revisions ?? 0)
        });
        writeAudit(tx, {
          action: 'Payroll calculated',
          entity: 'Payroll',
          entityId: runId,
          summary: `${period}: ${lines.length} employees, net K${run.totals.net}`
        });
        return runId;
      });
      return {
        ok: true,
        id
      };
    } catch (error) {
      return failure(error, 'Payroll could not be calculated.');
    }
  };
  const setRunStatus = async (runId: string, from: PayrollRun['status'], to: PayrollRun['status'], action: string, extra: Partial<PayrollRun> = {}, check?: (run: PayrollRun) => void) => {
    try {
      actor();
      await runTransaction(db, async tx => {
        const ref = doc(db, COL.runs, runId);
        const snap = await tx.get(ref);
        const run = snap.data() as PayrollRun | undefined;
        if (!run) throw new Error('Payroll run not found.');
        if (run.status !== from) throw new Error(`This payroll is ${run.status.toLowerCase()}, not ${from.toLowerCase()}.`);
        check?.(run);
        tx.update(ref, clean({
          status: to,
          ...extra
        }));
        writeAudit(tx, {
          action,
          entity: 'Payroll',
          entityId: runId,
          summary: `${run.period} — ${run.totals.employees} employees, net K${run.totals.net}`
        });
      });
      return {
        ok: true,
        id: runId
      };
    } catch (error) {
      return failure(error, `${action} failed.`);
    }
  };
  const returnToDraft: HrPayrollContextValue['returnToDraft'] = runId => setRunStatus(runId, 'Approved', 'Draft', 'Payroll returned to draft');
  const approvePayroll: HrPayrollContextValue['approvePayroll'] = runId => setRunStatus(runId, 'Draft', 'Approved', 'Payroll approved', {
    approvedAt: new Date().toISOString(),
    approvedByName: currentUser?.fullName
  }, run => {
    const blocked = run.lines.filter(line => line.errors.length > 0);
    if (blocked.length) throw new Error(`Fix ${blocked.length} employee error(s) before approving (e.g. ${blocked[0].name}: ${blocked[0].errors[0]}).`);
  });
  const payEmployees: HrPayrollContextValue['payEmployees'] = async runId => {
    try {
      const user = actor();
      const result = await runTransaction(db, async tx => {
        const runRef = doc(db, COL.runs, runId);
        const runSnap = await tx.get(runRef);
        const run = runSnap.data() as PayrollRun | undefined;
        if (!run) throw new Error('Payroll run not found.');
        if (run.status === 'Processed' || run.status === 'Paid') return {
          already: true,
          reference: run.reference,
          count: run.lines.length
        };
        if (run.status !== 'Approved') throw new Error('Approve the payroll before paying employees.');
        const year = run.period.slice(0, 4);
        const periodRef = doc(db, COL.periods, run.period);
        const runCounterRef = doc(db, COL.counters, `hrPayrollRun-${year}`);
        const slipCounterRef = doc(db, COL.counters, `hrPayslip-${year}`);
        const slipRefs = run.lines.map(line => doc(db, COL.payslips, `${runId}__${line.employeeId}`));
        const [periodSnap, runCounter, slipCounter, ...slipSnaps] = await Promise.all([tx.get(periodRef), tx.get(runCounterRef), tx.get(slipCounterRef), ...slipRefs.map(ref => tx.get(ref))]);
        if (periodSnap.data()?.activeRunId !== runId) throw new Error('A different payroll is active for this month.');
        if (slipSnaps.some(snap => snap.exists())) throw new Error('Payslips already exist for this payroll.');
        const now = new Date().toISOString();
        const runNo = (Number(runCounter.data()?.last) || 0) + 1;
        const reference = `HMS-PR-${year}-${String(runNo).padStart(4, '0')}`;
        let slipNo = Number(slipCounter.data()?.last) || 0;
        // Refresh each employee's leave position from the latest records and freeze it with the payslip.
        const lines = run.lines.map(line => {
          const employee = employees.find(e => e.id === line.employeeId);
          const leave = employee ? leaveSnapshot(employee, leaveRequests, settings, run.period) : line.leave;
          return leave ? {
            ...line,
            leave
          } : line;
        });
        lines.forEach((line, index) => {
          slipNo += 1;
          const payslip: Payslip = {
            id: slipRefs[index].id,
            payslipNumber: `${settings.payslipPrefix}-${year}-${String(slipNo).padStart(4, '0')}`,
            runId,
            runReference: reference,
            period: run.period,
            employeeId: line.employeeId,
            paymentDate: run.paymentDate,
            data: line,
            status: 'Issued',
            generatedAt: now,
            generatedByName: user.fullName
          };
          tx.set(slipRefs[index], clean(payslip));
          line.adjustmentIds.forEach(adjId => tx.update(doc(db, COL.adjustments, adjId), {
            runId
          }));
        });
        tx.set(runCounterRef, {
          last: runNo,
          updatedAt: now
        });
        tx.set(slipCounterRef, {
          last: slipNo,
          updatedAt: now
        });
        tx.update(runRef, clean({
          status: 'Processed',
          reference,
          processedAt: now,
          processedByName: user.fullName,
          lines
        }));
        writeAudit(tx, {
          action: 'Payroll processed (PAY)',
          entity: 'Payroll',
          entityId: runId,
          summary: `${reference}: ${run.lines.length} payslips, net K${run.totals.net}`
        });
        return {
          already: false,
          reference,
          count: run.lines.length
        };
      });
      return {
        ok: true,
        id: runId,
        message: result.already ? `Already processed as ${result.reference} — no duplicate transactions or payslips were created.` : `${result.reference}: ${result.count} payroll transactions saved and ${result.count} payslips generated.`
      };
    } catch (error) {
      return failure(error, 'Payroll could not be processed.');
    }
  };
  const confirmPayment: HrPayrollContextValue['confirmPayment'] = async (runId, paymentReference, paidDate) => {
    try {
      const user = actor();
      if (!paymentReference.trim()) return {
        ok: false,
        error: 'Enter the bank / payment reference.'
      };
      await runTransaction(db, async tx => {
        const runRef = doc(db, COL.runs, runId);
        const runSnap = await tx.get(runRef);
        const run = runSnap.data() as PayrollRun | undefined;
        if (!run) throw new Error('Payroll run not found.');
        if (run.status === 'Paid') return;
        if (run.status !== 'Processed') throw new Error('Only processed payroll can be confirmed as paid.');
        const recoveries = run.lines.flatMap(line => line.loanRecoveries);
        const loanRefs = recoveries.map(r => doc(db, COL.loans, r.loanId));
        const loanSnaps = await Promise.all(loanRefs.map(ref => tx.get(ref)));
        const now = new Date().toISOString();
        recoveries.forEach((recovery, index) => {
          const loan = loanSnaps[index].data() as EmployeeLoan | undefined;
          if (!loan || loan.repayments?.some(r => r.runId === runId)) return;
          const amountN = Math.min(Math.round(recovery.amount * 100), Math.round(loan.balance * 100));
          const balance = Math.round(loan.balance * 100 - amountN) / 100;
          tx.update(loanRefs[index], clean({
            balance,
            status: balance <= 0 ? 'Settled' : loan.status,
            repayments: [...(loan.repayments ?? []), {
              runId,
              period: run.period,
              amount: amountN / 100,
              appliedAt: now
            }]
          }));
        });
        run.lines.forEach(line => tx.update(doc(db, COL.payslips, `${runId}__${line.employeeId}`), {
          status: 'Paid'
        }));
        tx.update(runRef, {
          status: 'Paid',
          paidAt: paidDate,
          paidByName: user.fullName,
          paymentReference: paymentReference.trim(),
          loansApplied: true
        });
        writeAudit(tx, {
          action: 'Payroll payment confirmed',
          entity: 'Payroll',
          entityId: runId,
          summary: `${run.reference} paid ${paidDate}, ref ${paymentReference.trim()}`
        });
      });
      return {
        ok: true,
        id: runId
      };
    } catch (error) {
      return failure(error, 'Payment could not be confirmed.');
    }
  };
  const reversePayroll: HrPayrollContextValue['reversePayroll'] = async (runId, reason) => {
    try {
      const user = actor();
      if (!reason.trim()) return {
        ok: false,
        error: 'A reason for the reversal is required.'
      };
      await runTransaction(db, async tx => {
        const runRef = doc(db, COL.runs, runId);
        const runSnap = await tx.get(runRef);
        const run = runSnap.data() as PayrollRun | undefined;
        if (!run) throw new Error('Payroll run not found.');
        if (run.status !== 'Processed') throw new Error('Only processed, unpaid payroll can be reversed.');
        const periodRef = doc(db, COL.periods, run.period);
        const periodSnap = await tx.get(periodRef);
        const now = new Date().toISOString();
        run.lines.forEach(line => {
          tx.update(doc(db, COL.payslips, `${runId}__${line.employeeId}`), {
            status: 'Void'
          });
          line.adjustmentIds.forEach(adjId => tx.update(doc(db, COL.adjustments, adjId), {
            runId: deleteField()
          }));
        });
        tx.update(runRef, {
          status: 'Reversed',
          reversedAt: now,
          reversedByName: user.fullName,
          reversalReason: reason.trim()
        });
        tx.set(periodRef, {
          activeRunId: null,
          revisions: periodSnap.data()?.revisions ?? run.revision
        });
        writeAudit(tx, {
          action: 'Payroll reversed',
          entity: 'Payroll',
          entityId: runId,
          summary: `${run.reference}: ${reason.trim()}`
        });
      });
      return {
        ok: true,
        id: runId
      };
    } catch (error) {
      return failure(error, 'The payroll could not be reversed.');
    }
  };
  const isReady = isAdmin && ['settings', COL.employees, COL.departments, COL.runs].every(key => loaded[key]);
  const value = useMemo<HrPayrollContextValue>(() => ({
    isReady,
    employees,
    departments,
    positions,
    attendance,
    leaveRequests,
    loans,
    adjustments,
    payrollRuns,
    payslips,
    documents,
    auditLogs,
    settings,
    saveDepartment,
    deleteDepartment,
    savePosition,
    deletePosition,
    registerEmployee,
    updateEmployee,
    changeSalary,
    recordEmploymentEvent,
    addAttendance,
    decideAttendance,
    deleteAttendance,
    addLeave,
    decideLeave,
    setLeaveOpening,
    addLeaveAdjustment,
    addLoan,
    cancelLoan,
    addAdjustment,
    deleteAdjustment,
    uploadDocument,
    deleteDocument,
    saveSettings,
    addRuleSet,
    deleteRuleSet,
    calculatePayroll,
    returnToDraft,
    approvePayroll,
    payEmployees,
    confirmPayment,
    reversePayroll
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isReady, employees, departments, positions, attendance, leaveRequests, loans, adjustments, payrollRuns, payslips, documents, auditLogs, settings, currentUser]);
  return <HrPayrollContext.Provider value={value} data-id="element-130" data-mp="3jhmwihseef6o">{children}</HrPayrollContext.Provider>;
}
export function useHrPayroll() {
  const context = useContext(HrPayrollContext);
  if (!context) throw new Error('useHrPayroll must be used within HrPayrollProvider');
  return context;
}